import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── Particle canvas background ────────────────────────────────────────────
function ParticleCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const dots = Array.from({ length: 80 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 3 + 1,
      dx: (Math.random() - 0.5) * 0.5,
      dy: (Math.random() - 0.5) * 0.5,
      o: Math.random() * 0.5 + 0.2,
    }));
    function draw() {
      ctx.clearRect(0, 0, W, H);
      dots.forEach(d => {
        d.x += d.dx; d.y += d.dy;
        if (d.x < 0 || d.x > W) d.dx *= -1;
        if (d.y < 0 || d.y > H) d.dy *= -1;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(215,38,56,${d.o})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    }
    draw();
    const onResize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', onResize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }} />;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', business_name: '', website: '', email: '', password: '', unit_system: 'imperial' });
  const [healthConsent, setHealthConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms,     setShowTerms]     = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function update(field) {
    return (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!termsAccepted) { setError('Please read and accept the Terms of Service to continue.'); return; }
    if (!healthConsent) { setError('Please consent to health data processing to continue.'); return; }
    setError(''); setLoading(true);
    try {
      await register(form.name, form.email, form.password, form.business_name, form.unit_system, form.website, true);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {showTerms && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: '#080808', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
            <button onClick={() => setShowTerms(false)} style={{ background: 'none', border: 'none', color: '#666', fontSize: 20, cursor: 'pointer', padding: 0, fontFamily: 'inherit', lineHeight: 1 }}>✕</button>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Terms of Service</span>
            <span style={{ width: 24 }} />
          </div>
          <iframe src="/terms" style={{ flex: 1, border: 'none', minHeight: 0 }} title="Terms of Service" />
          <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => { setTermsAccepted(true); setShowTerms(false); }}
              style={{ width: '100%', padding: 16, background: '#D72638', border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 20px rgba(215,38,56,0.35)' }}
            >
              I Accept the Terms of Service
            </button>
          </div>
        </div>
      )}
    <div style={{ minHeight: '100vh', backgroundColor: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '24px', paddingTop: '40px', position: 'relative', overflow: 'hidden' }}>

      <ParticleCanvas />
      <div style={{ position: 'fixed', top: -160, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(215,38,56,0.18) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/icons/icon-menu-logo.png" alt="Win-R"
            style={{ height: 64, objectFit: 'contain', marginBottom: 14 }}
            onError={e => e.target.style.display = 'none'} />
          <h1 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: -0.5 }}>Create Account</h1>
          <p style={{ margin: 0, color: '#555', fontSize: 14 }}>Set up your trainer profile</p>
        </div>

        {error && (
          <div style={{ background: 'rgba(215,38,56,0.1)', border: '1px solid rgba(215,38,56,0.3)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <span style={{ color: '#D72638', fontSize: 13 }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          <div>
            <label style={labelStyle}>Full Name</label>
            <input style={inputStyle} type="text" value={form.name} onChange={update('name')} placeholder="Jane Smith" required />
          </div>

          <div>
            <label style={labelStyle}>Business Name <span style={{ color: '#444', fontWeight: 400 }}>(optional)</span></label>
            <input style={inputStyle} type="text" value={form.business_name} onChange={update('business_name')} placeholder="Smith Fitness" />
          </div>

          <div>
            <label style={labelStyle}>Website <span style={{ color: '#444', fontWeight: 400 }}>(optional)</span></label>
            <input style={inputStyle} type="url" value={form.website} onChange={update('website')} placeholder="https://yourwebsite.com" />
          </div>

          <div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" required autoComplete="email" />
          </div>

          <div>
            <label style={labelStyle}>Password</label>
            <input style={inputStyle} type="password" value={form.password} onChange={update('password')} placeholder="••••••••" required autoComplete="new-password" minLength={6} />
          </div>

          <div>
            <label style={labelStyle}>Measurement System</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
              {[
                { value: 'imperial', label: '🇺🇸 US', sub: 'lbs · ft / in' },
                { value: 'metric',   label: '🇬🇧 UK', sub: 'kg · cm' },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setForm(f => ({ ...f, unit_system: opt.value }))}
                  style={{
                    minHeight: 64, borderRadius: 12,
                    border: '2px solid',
                    borderColor: form.unit_system === opt.value ? '#D72638' : 'rgba(255,255,255,0.1)',
                    background: form.unit_system === opt.value ? 'rgba(215,38,56,0.12)' : 'rgba(255,255,255,0.04)',
                    cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                    transition: 'border-color 0.15s, background 0.15s',
                  }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: form.unit_system === opt.value ? '#D72638' : '#fff' }}>{opt.label}</span>
                  <span style={{ fontSize: 12, color: '#555' }}>{opt.sub}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setShowTerms(true)}
              style={{
                width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
                border: '2px solid', borderColor: termsAccepted ? '#D72638' : 'rgba(255,255,255,0.2)',
                background: termsAccepted ? '#D72638' : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {termsAccepted && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>&#10003;</span>}
            </button>
            <span style={{ fontSize: 12, color: '#555', lineHeight: '18px' }}>
              I have read and accept the{' '}
              <button type="button" onClick={() => setShowTerms(true)} style={{ background: 'none', border: 'none', color: '#D72638', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 12 }}>
                Terms of Service
              </button>
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setHealthConsent(v => !v)}
              style={{
                width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
                border: '2px solid', borderColor: healthConsent ? '#D72638' : 'rgba(255,255,255,0.2)',
                background: healthConsent ? '#D72638' : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {healthConsent && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>✓</span>}
            </button>
            <span style={{ fontSize: 12, color: '#555', lineHeight: '18px' }}>
              I consent to Win/R processing my health and fitness data (body measurements, workout logs, nutrition records) to deliver the Service.{' '}
              <a href="/privacy" style={{ color: '#D72638', textDecoration: 'underline' }}>Privacy Policy</a>
            </span>
          </div>

          <button className="btn btn-primary" type="submit" disabled={loading || !healthConsent || !termsAccepted}
            style={{ marginTop: 4, boxShadow: '0 4px 20px rgba(215,38,56,0.35)', opacity: (loading || !healthConsent || !termsAccepted) ? 0.5 : 1 }}>
            {loading ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Creating account...</> : 'Create Account'}
          </button>

        </form>

        <p style={{ textAlign: 'center', marginTop: 20, color: '#444', fontSize: 14 }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#D72638', fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
        </p>

      </div>
    </div>
    </>
  );
}

const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: '#555', letterSpacing: 0.4,
  textTransform: 'uppercase', marginBottom: 6,
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12, padding: '14px 16px',
  fontSize: 15, color: '#fff',
};
